var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
/**
* @vue/reactivity v3.4.26
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
import { NOOP, extend, isArray, isSymbol, isMap, isIntegerKey, hasOwn, hasChanged, isObject, makeMap, capitalize, toRawType, def, isFunction } from '@vue/shared';
function warn(msg) {
    var args = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        args[_i - 1] = arguments[_i];
    }
    console.warn.apply(console, __spreadArray(["[Vue warn] ".concat(msg)], __read(args), false));
}
var activeEffectScope;
var EffectScope = /** @class */ (function () {
    function EffectScope(detached) {
        if (detached === void 0) { detached = false; }
        this.detached = detached;
        /**
         * @internal
         */
        this._active = true;
        /**
         * @internal
         */
        this.effects = [];
        /**
         * @internal
         */
        this.cleanups = [];
        this.parent = activeEffectScope;
        if (!detached && activeEffectScope) {
            this.index = (activeEffectScope.scopes || (activeEffectScope.scopes = [])).push(this) - 1;
        }
    }
    Object.defineProperty(EffectScope.prototype, "active", {
        get: function () {
            return this._active;
        },
        enumerable: false,
        configurable: true
    });
    EffectScope.prototype.run = function (fn) {
        if (this._active) {
            var currentEffectScope = activeEffectScope;
            try {
                activeEffectScope = this;
                return fn();
            }
            finally {
                activeEffectScope = currentEffectScope;
            }
        }
        else if (!!(process.env.NODE_ENV !== "production")) {
            warn("cannot run an inactive effect scope.");
        }
    };
    /**
     * This should only be called on non-detached scopes
     * @internal
     */
    EffectScope.prototype.on = function () {
        activeEffectScope = this;
    };
    /**
     * This should only be called on non-detached scopes
     * @internal
     */
    EffectScope.prototype.off = function () {
        activeEffectScope = this.parent;
    };
    EffectScope.prototype.stop = function (fromParent) {
        if (this._active) {
            var i = void 0, l = void 0;
            for (i = 0, l = this.effects.length; i < l; i++) {
                this.effects[i].stop();
            }
            for (i = 0, l = this.cleanups.length; i < l; i++) {
                this.cleanups[i]();
            }
            if (this.scopes) {
                for (i = 0, l = this.scopes.length; i < l; i++) {
                    this.scopes[i].stop(true);
                }
            }
            if (!this.detached && this.parent && !fromParent) {
                var last = this.parent.scopes.pop();
                if (last && last !== this) {
                    this.parent.scopes[this.index] = last;
                    last.index = this.index;
                }
            }
            this.parent = void 0;
            this._active = false;
        }
    };
    return EffectScope;
}());
function effectScope(detached) {
    return new EffectScope(detached);
}
function recordEffectScope(effect, scope) {
    if (scope === void 0) { scope = activeEffectScope; }
    if (scope && scope.active) {
        scope.effects.push(effect);
    }
}
function getCurrentScope() {
    return activeEffectScope;
}
function onScopeDispose(fn) {
    if (activeEffectScope) {
        activeEffectScope.cleanups.push(fn);
    }
    else if (!!(process.env.NODE_ENV !== "production")) {
        warn("onScopeDispose() is called when there is no active effect scope to be associated with.");
    }
}
var activeEffect;
var ReactiveEffect = /** @class */ (function () {
    function ReactiveEffect(fn, trigger, scheduler, scope) {
        this.fn = fn;
        this.trigger = trigger;
        this.scheduler = scheduler;
        this.active = true;
        this.deps = [];
        /**
         * @internal
         */
        this._dirtyLevel = 4;
        /**
         * @internal
         */
        this._trackId = 0;
        /**
         * @internal
         */
        this._runnings = 0;
        /**
         * @internal
         */
        this._shouldSchedule = false;
        /**
         * @internal
         */
        this._depsLength = 0;
        recordEffectScope(this, scope);
    }
    Object.defineProperty(ReactiveEffect.prototype, "dirty", {
        get: function () {
            if (this._dirtyLevel === 2 || this._dirtyLevel === 3) {
                this._dirtyLevel = 1;
                pauseTracking();
                for (var i = 0; i < this._depsLength; i++) {
                    var dep = this.deps[i];
                    if (dep.computed) {
                        triggerComputed(dep.computed);
                        if (this._dirtyLevel >= 4) {
                            break;
                        }
                    }
                }
                if (this._dirtyLevel === 1) {
                    this._dirtyLevel = 0;
                }
                resetTracking();
            }
            return this._dirtyLevel >= 4;
        },
        set: function (v) {
            this._dirtyLevel = v ? 4 : 0;
        },
        enumerable: false,
        configurable: true
    });
    ReactiveEffect.prototype.run = function () {
        this._dirtyLevel = 0;
        if (!this.active) {
            return this.fn();
        }
        var lastShouldTrack = shouldTrack;
        var lastEffect = activeEffect;
        try {
            shouldTrack = true;
            activeEffect = this;
            this._runnings++;
            preCleanupEffect(this);
            return this.fn();
        }
        finally {
            postCleanupEffect(this);
            this._runnings--;
            activeEffect = lastEffect;
            shouldTrack = lastShouldTrack;
        }
    };
    ReactiveEffect.prototype.stop = function () {
        if (this.active) {
            preCleanupEffect(this);
            postCleanupEffect(this);
            this.onStop && this.onStop();
            this.active = false;
        }
    };
    return ReactiveEffect;
}());
function triggerComputed(computed) {
    return computed.value;
}
function preCleanupEffect(effect2) {
    effect2._trackId++;
    effect2._depsLength = 0;
}
function postCleanupEffect(effect2) {
    if (effect2.deps.length > effect2._depsLength) {
        for (var i = effect2._depsLength; i < effect2.deps.length; i++) {
            cleanupDepEffect(effect2.deps[i], effect2);
        }
        effect2.deps.length = effect2._depsLength;
    }
}
function cleanupDepEffect(dep, effect2) {
    var trackId = dep.get(effect2);
    if (trackId !== void 0 && effect2._trackId !== trackId) {
        dep.delete(effect2);
        if (dep.size === 0) {
            dep.cleanup();
        }
    }
}
function effect(fn, options) {
    if (fn.effect instanceof ReactiveEffect) {
        fn = fn.effect.fn;
    }
    var _effect = new ReactiveEffect(fn, NOOP, function () {
        if (_effect.dirty) {
            _effect.run();
        }
    });
    if (options) {
        extend(_effect, options);
        if (options.scope)
            recordEffectScope(_effect, options.scope);
    }
    if (!options || !options.lazy) {
        _effect.run();
    }
    var runner = _effect.run.bind(_effect);
    runner.effect = _effect;
    return runner;
}
function stop(runner) {
    runner.effect.stop();
}
var shouldTrack = true;
var pauseScheduleStack = 0;
var trackStack = [];
function pauseTracking() {
    trackStack.push(shouldTrack);
    shouldTrack = false;
}
function enableTracking() {
    trackStack.push(shouldTrack);
    shouldTrack = true;
}
function resetTracking() {
    var last = trackStack.pop();
    shouldTrack = last === void 0 ? true : last;
}
function pauseScheduling() {
    pauseScheduleStack++;
}
function resetScheduling() {
    pauseScheduleStack--;
    while (!pauseScheduleStack && queueEffectSchedulers.length) {
        queueEffectSchedulers.shift()();
    }
}
function trackEffect(effect2, dep, debuggerEventExtraInfo) {
    var _a;
    if (dep.get(effect2) !== effect2._trackId) {
        dep.set(effect2, effect2._trackId);
        var oldDep = effect2.deps[effect2._depsLength];
        if (oldDep !== dep) {
            if (oldDep) {
                cleanupDepEffect(oldDep, effect2);
            }
            effect2.deps[effect2._depsLength++] = dep;
        }
        else {
            effect2._depsLength++;
        }
        if (!!(process.env.NODE_ENV !== "production")) {
            (_a = effect2.onTrack) == null ? void 0 : _a.call(effect2, extend({ effect: effect2 }, debuggerEventExtraInfo));
        }
    }
}
var queueEffectSchedulers = [];
function triggerEffects(dep, dirtyLevel, debuggerEventExtraInfo) {
    var e_1, _b;
    var _a;
    pauseScheduling();
    try {
        for (var _c = __values(dep.keys()), _d = _c.next(); !_d.done; _d = _c.next()) {
            var effect2 = _d.value;
            var tracking = void 0;
            if (effect2._dirtyLevel < dirtyLevel && (tracking != null ? tracking : tracking = dep.get(effect2) === effect2._trackId)) {
                effect2._shouldSchedule || (effect2._shouldSchedule = effect2._dirtyLevel === 0);
                effect2._dirtyLevel = dirtyLevel;
            }
            if (effect2._shouldSchedule && (tracking != null ? tracking : tracking = dep.get(effect2) === effect2._trackId)) {
                if (!!(process.env.NODE_ENV !== "production")) {
                    (_a = effect2.onTrigger) == null ? void 0 : _a.call(effect2, extend({ effect: effect2 }, debuggerEventExtraInfo));
                }
                effect2.trigger();
                if ((!effect2._runnings || effect2.allowRecurse) && effect2._dirtyLevel !== 2) {
                    effect2._shouldSchedule = false;
                    if (effect2.scheduler) {
                        queueEffectSchedulers.push(effect2.scheduler);
                    }
                }
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
        }
        finally { if (e_1) throw e_1.error; }
    }
    resetScheduling();
}
var createDep = function (cleanup, computed) {
    var dep = /* @__PURE__ */ new Map();
    dep.cleanup = cleanup;
    dep.computed = computed;
    return dep;
};
var targetMap = /* @__PURE__ */ new WeakMap();
var ITERATE_KEY = Symbol(!!(process.env.NODE_ENV !== "production") ? "iterate" : "");
var MAP_KEY_ITERATE_KEY = Symbol(!!(process.env.NODE_ENV !== "production") ? "Map key iterate" : "");
function track(target, type, key) {
    if (shouldTrack && activeEffect) {
        var depsMap_1 = targetMap.get(target);
        if (!depsMap_1) {
            targetMap.set(target, depsMap_1 = /* @__PURE__ */ new Map());
        }
        var dep = depsMap_1.get(key);
        if (!dep) {
            depsMap_1.set(key, dep = createDep(function () { return depsMap_1.delete(key); }));
        }
        trackEffect(activeEffect, dep, !!(process.env.NODE_ENV !== "production") ? {
            target: target,
            type: type,
            key: key
        } : void 0);
    }
}
function trigger(target, type, key, newValue, oldValue, oldTarget) {
    var e_2, _b;
    var depsMap = targetMap.get(target);
    if (!depsMap) {
        return;
    }
    var deps = [];
    if (type === "clear") {
        deps = __spreadArray([], __read(depsMap.values()), false);
    }
    else if (key === "length" && isArray(target)) {
        var newLength_1 = Number(newValue);
        depsMap.forEach(function (dep, key2) {
            if (key2 === "length" || !isSymbol(key2) && key2 >= newLength_1) {
                deps.push(dep);
            }
        });
    }
    else {
        if (key !== void 0) {
            deps.push(depsMap.get(key));
        }
        switch (type) {
            case "add":
                if (!isArray(target)) {
                    deps.push(depsMap.get(ITERATE_KEY));
                    if (isMap(target)) {
                        deps.push(depsMap.get(MAP_KEY_ITERATE_KEY));
                    }
                }
                else if (isIntegerKey(key)) {
                    deps.push(depsMap.get("length"));
                }
                break;
            case "delete":
                if (!isArray(target)) {
                    deps.push(depsMap.get(ITERATE_KEY));
                    if (isMap(target)) {
                        deps.push(depsMap.get(MAP_KEY_ITERATE_KEY));
                    }
                }
                break;
            case "set":
                if (isMap(target)) {
                    deps.push(depsMap.get(ITERATE_KEY));
                }
                break;
        }
    }
    pauseScheduling();
    try {
        for (var deps_1 = __values(deps), deps_1_1 = deps_1.next(); !deps_1_1.done; deps_1_1 = deps_1.next()) {
            var dep = deps_1_1.value;
            if (dep) {
                triggerEffects(dep, 4, !!(process.env.NODE_ENV !== "production") ? {
                    target: target,
                    type: type,
                    key: key,
                    newValue: newValue,
                    oldValue: oldValue,
                    oldTarget: oldTarget
                } : void 0);
            }
        }
    }
    catch (e_2_1) { e_2 = { error: e_2_1 }; }
    finally {
        try {
            if (deps_1_1 && !deps_1_1.done && (_b = deps_1.return)) _b.call(deps_1);
        }
        finally { if (e_2) throw e_2.error; }
    }
    resetScheduling();
}
function getDepFromReactive(object, key) {
    var depsMap = targetMap.get(object);
    return depsMap && depsMap.get(key);
}
var isNonTrackableKeys = /* @__PURE__ */ makeMap("__proto__,__v_isRef,__isVue");
var builtInSymbols = new Set(
/* @__PURE__ */ Object.getOwnPropertyNames(Symbol).filter(function (key) { return key !== "arguments" && key !== "caller"; }).map(function (key) { return Symbol[key]; }).filter(isSymbol));
var arrayInstrumentations = /* @__PURE__ */ createArrayInstrumentations();
function createArrayInstrumentations() {
    var instrumentations = {};
    ["includes", "indexOf", "lastIndexOf"].forEach(function (key) {
        instrumentations[key] = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            var arr = toRaw(this);
            for (var i = 0, l = this.length; i < l; i++) {
                track(arr, "get", i + "");
            }
            var res = arr[key].apply(arr, __spreadArray([], __read(args), false));
            if (res === -1 || res === false) {
                return arr[key].apply(arr, __spreadArray([], __read(args.map(toRaw)), false));
            }
            else {
                return res;
            }
        };
    });
    ["push", "pop", "shift", "unshift", "splice"].forEach(function (key) {
        instrumentations[key] = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            pauseTracking();
            pauseScheduling();
            var res = toRaw(this)[key].apply(this, args);
            resetScheduling();
            resetTracking();
            return res;
        };
    });
    return instrumentations;
}
function hasOwnProperty(key) {
    if (!isSymbol(key))
        key = String(key);
    var obj = toRaw(this);
    track(obj, "has", key);
    return obj.hasOwnProperty(key);
}
var BaseReactiveHandler = /** @class */ (function () {
    function BaseReactiveHandler(_isReadonly, _isShallow) {
        if (_isReadonly === void 0) { _isReadonly = false; }
        if (_isShallow === void 0) { _isShallow = false; }
        this._isReadonly = _isReadonly;
        this._isShallow = _isShallow;
    }
    BaseReactiveHandler.prototype.get = function (target, key, receiver) {
        var isReadonly2 = this._isReadonly, isShallow2 = this._isShallow;
        if (key === "__v_isReactive") {
            return !isReadonly2;
        }
        else if (key === "__v_isReadonly") {
            return isReadonly2;
        }
        else if (key === "__v_isShallow") {
            return isShallow2;
        }
        else if (key === "__v_raw") {
            if (receiver === (isReadonly2 ? isShallow2 ? shallowReadonlyMap : readonlyMap : isShallow2 ? shallowReactiveMap : reactiveMap).get(target) || // receiver is not the reactive proxy, but has the same prototype
                // this means the reciever is a user proxy of the reactive proxy
                Object.getPrototypeOf(target) === Object.getPrototypeOf(receiver)) {
                return target;
            }
            return;
        }
        var targetIsArray = isArray(target);
        if (!isReadonly2) {
            if (targetIsArray && hasOwn(arrayInstrumentations, key)) {
                return Reflect.get(arrayInstrumentations, key, receiver);
            }
            if (key === "hasOwnProperty") {
                return hasOwnProperty;
            }
        }
        var res = Reflect.get(target, key, receiver);
        if (isSymbol(key) ? builtInSymbols.has(key) : isNonTrackableKeys(key)) {
            return res;
        }
        if (!isReadonly2) {
            track(target, "get", key);
        }
        if (isShallow2) {
            return res;
        }
        if (isRef(res)) {
            return targetIsArray && isIntegerKey(key) ? res : res.value;
        }
        if (isObject(res)) {
            return isReadonly2 ? readonly(res) : reactive(res);
        }
        return res;
    };
    return BaseReactiveHandler;
}());
var MutableReactiveHandler = /** @class */ (function (_super) {
    __extends(MutableReactiveHandler, _super);
    function MutableReactiveHandler(isShallow2) {
        if (isShallow2 === void 0) { isShallow2 = false; }
        return _super.call(this, false, isShallow2) || this;
    }
    MutableReactiveHandler.prototype.set = function (target, key, value, receiver) {
        var oldValue = target[key];
        if (!this._isShallow) {
            var isOldValueReadonly = isReadonly(oldValue);
            if (!isShallow(value) && !isReadonly(value)) {
                oldValue = toRaw(oldValue);
                value = toRaw(value);
            }
            if (!isArray(target) && isRef(oldValue) && !isRef(value)) {
                if (isOldValueReadonly) {
                    return false;
                }
                else {
                    oldValue.value = value;
                    return true;
                }
            }
        }
        var hadKey = isArray(target) && isIntegerKey(key) ? Number(key) < target.length : hasOwn(target, key);
        var result = Reflect.set(target, key, value, receiver);
        if (target === toRaw(receiver)) {
            if (!hadKey) {
                trigger(target, "add", key, value);
            }
            else if (hasChanged(value, oldValue)) {
                trigger(target, "set", key, value, oldValue);
            }
        }
        return result;
    };
    MutableReactiveHandler.prototype.deleteProperty = function (target, key) {
        var hadKey = hasOwn(target, key);
        var oldValue = target[key];
        var result = Reflect.deleteProperty(target, key);
        if (result && hadKey) {
            trigger(target, "delete", key, void 0, oldValue);
        }
        return result;
    };
    MutableReactiveHandler.prototype.has = function (target, key) {
        var result = Reflect.has(target, key);
        if (!isSymbol(key) || !builtInSymbols.has(key)) {
            track(target, "has", key);
        }
        return result;
    };
    MutableReactiveHandler.prototype.ownKeys = function (target) {
        track(target, "iterate", isArray(target) ? "length" : ITERATE_KEY);
        return Reflect.ownKeys(target);
    };
    return MutableReactiveHandler;
}(BaseReactiveHandler));
var ReadonlyReactiveHandler = /** @class */ (function (_super) {
    __extends(ReadonlyReactiveHandler, _super);
    function ReadonlyReactiveHandler(isShallow2) {
        if (isShallow2 === void 0) { isShallow2 = false; }
        return _super.call(this, true, isShallow2) || this;
    }
    ReadonlyReactiveHandler.prototype.set = function (target, key) {
        if (!!(process.env.NODE_ENV !== "production")) {
            warn("Set operation on key \"".concat(String(key), "\" failed: target is readonly."), target);
        }
        return true;
    };
    ReadonlyReactiveHandler.prototype.deleteProperty = function (target, key) {
        if (!!(process.env.NODE_ENV !== "production")) {
            warn("Delete operation on key \"".concat(String(key), "\" failed: target is readonly."), target);
        }
        return true;
    };
    return ReadonlyReactiveHandler;
}(BaseReactiveHandler));
var mutableHandlers = /* @__PURE__ */ new MutableReactiveHandler();
var readonlyHandlers = /* @__PURE__ */ new ReadonlyReactiveHandler();
var shallowReactiveHandlers = /* @__PURE__ */ new MutableReactiveHandler(true);
var shallowReadonlyHandlers = /* @__PURE__ */ new ReadonlyReactiveHandler(true);
var toShallow = function (value) { return value; };
var getProto = function (v) { return Reflect.getPrototypeOf(v); };
function get(target, key, isReadonly, isShallow) {
    if (isReadonly === void 0) { isReadonly = false; }
    if (isShallow === void 0) { isShallow = false; }
    target = target["__v_raw"];
    var rawTarget = toRaw(target);
    var rawKey = toRaw(key);
    if (!isReadonly) {
        if (hasChanged(key, rawKey)) {
            track(rawTarget, "get", key);
        }
        track(rawTarget, "get", rawKey);
    }
    var has2 = getProto(rawTarget).has;
    var wrap = isShallow ? toShallow : isReadonly ? toReadonly : toReactive;
    if (has2.call(rawTarget, key)) {
        return wrap(target.get(key));
    }
    else if (has2.call(rawTarget, rawKey)) {
        return wrap(target.get(rawKey));
    }
    else if (target !== rawTarget) {
        target.get(key);
    }
}
function has(key, isReadonly) {
    if (isReadonly === void 0) { isReadonly = false; }
    var target = this["__v_raw"];
    var rawTarget = toRaw(target);
    var rawKey = toRaw(key);
    if (!isReadonly) {
        if (hasChanged(key, rawKey)) {
            track(rawTarget, "has", key);
        }
        track(rawTarget, "has", rawKey);
    }
    return key === rawKey ? target.has(key) : target.has(key) || target.has(rawKey);
}
function size(target, isReadonly) {
    if (isReadonly === void 0) { isReadonly = false; }
    target = target["__v_raw"];
    !isReadonly && track(toRaw(target), "iterate", ITERATE_KEY);
    return Reflect.get(target, "size", target);
}
function add(value) {
    value = toRaw(value);
    var target = toRaw(this);
    var proto = getProto(target);
    var hadKey = proto.has.call(target, value);
    if (!hadKey) {
        target.add(value);
        trigger(target, "add", value, value);
    }
    return this;
}
function set(key, value) {
    value = toRaw(value);
    var target = toRaw(this);
    var _b = getProto(target), has2 = _b.has, get2 = _b.get;
    var hadKey = has2.call(target, key);
    if (!hadKey) {
        key = toRaw(key);
        hadKey = has2.call(target, key);
    }
    else if (!!(process.env.NODE_ENV !== "production")) {
        checkIdentityKeys(target, has2, key);
    }
    var oldValue = get2.call(target, key);
    target.set(key, value);
    if (!hadKey) {
        trigger(target, "add", key, value);
    }
    else if (hasChanged(value, oldValue)) {
        trigger(target, "set", key, value, oldValue);
    }
    return this;
}
function deleteEntry(key) {
    var target = toRaw(this);
    var _b = getProto(target), has2 = _b.has, get2 = _b.get;
    var hadKey = has2.call(target, key);
    if (!hadKey) {
        key = toRaw(key);
        hadKey = has2.call(target, key);
    }
    else if (!!(process.env.NODE_ENV !== "production")) {
        checkIdentityKeys(target, has2, key);
    }
    var oldValue = get2 ? get2.call(target, key) : void 0;
    var result = target.delete(key);
    if (hadKey) {
        trigger(target, "delete", key, void 0, oldValue);
    }
    return result;
}
function clear() {
    var target = toRaw(this);
    var hadItems = target.size !== 0;
    var oldTarget = !!(process.env.NODE_ENV !== "production") ? isMap(target) ? new Map(target) : new Set(target) : void 0;
    var result = target.clear();
    if (hadItems) {
        trigger(target, "clear", void 0, void 0, oldTarget);
    }
    return result;
}
function createForEach(isReadonly, isShallow) {
    return function forEach(callback, thisArg) {
        var observed = this;
        var target = observed["__v_raw"];
        var rawTarget = toRaw(target);
        var wrap = isShallow ? toShallow : isReadonly ? toReadonly : toReactive;
        !isReadonly && track(rawTarget, "iterate", ITERATE_KEY);
        return target.forEach(function (value, key) {
            return callback.call(thisArg, wrap(value), wrap(key), observed);
        });
    };
}
function createIterableMethod(method, isReadonly, isShallow) {
    return function () {
        var _b;
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var target = this["__v_raw"];
        var rawTarget = toRaw(target);
        var targetIsMap = isMap(rawTarget);
        var isPair = method === "entries" || method === Symbol.iterator && targetIsMap;
        var isKeyOnly = method === "keys" && targetIsMap;
        var innerIterator = target[method].apply(target, __spreadArray([], __read(args), false));
        var wrap = isShallow ? toShallow : isReadonly ? toReadonly : toReactive;
        !isReadonly && track(rawTarget, "iterate", isKeyOnly ? MAP_KEY_ITERATE_KEY : ITERATE_KEY);
        return _b = {
                // iterator protocol
                next: function () {
                    var _b = innerIterator.next(), value = _b.value, done = _b.done;
                    return done ? { value: value, done: done } : {
                        value: isPair ? [wrap(value[0]), wrap(value[1])] : wrap(value),
                        done: done
                    };
                }
            },
            // iterable protocol
            _b[Symbol.iterator] = function () {
                return this;
            },
            _b;
    };
}
function createReadonlyMethod(type) {
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (!!(process.env.NODE_ENV !== "production")) {
            var key = args[0] ? "on key \"".concat(args[0], "\" ") : "";
            warn("".concat(capitalize(type), " operation ").concat(key, "failed: target is readonly."), toRaw(this));
        }
        return type === "delete" ? false : type === "clear" ? void 0 : this;
    };
}
function createInstrumentations() {
    var mutableInstrumentations2 = {
        get: function (key) {
            return get(this, key);
        },
        get size() {
            return size(this);
        },
        has: has,
        add: add,
        set: set,
        delete: deleteEntry,
        clear: clear,
        forEach: createForEach(false, false)
    };
    var shallowInstrumentations2 = {
        get: function (key) {
            return get(this, key, false, true);
        },
        get size() {
            return size(this);
        },
        has: has,
        add: add,
        set: set,
        delete: deleteEntry,
        clear: clear,
        forEach: createForEach(false, true)
    };
    var readonlyInstrumentations2 = {
        get: function (key) {
            return get(this, key, true);
        },
        get size() {
            return size(this, true);
        },
        has: function (key) {
            return has.call(this, key, true);
        },
        add: createReadonlyMethod("add"),
        set: createReadonlyMethod("set"),
        delete: createReadonlyMethod("delete"),
        clear: createReadonlyMethod("clear"),
        forEach: createForEach(true, false)
    };
    var shallowReadonlyInstrumentations2 = {
        get: function (key) {
            return get(this, key, true, true);
        },
        get size() {
            return size(this, true);
        },
        has: function (key) {
            return has.call(this, key, true);
        },
        add: createReadonlyMethod("add"),
        set: createReadonlyMethod("set"),
        delete: createReadonlyMethod("delete"),
        clear: createReadonlyMethod("clear"),
        forEach: createForEach(true, true)
    };
    var iteratorMethods = [
        "keys",
        "values",
        "entries",
        Symbol.iterator
    ];
    iteratorMethods.forEach(function (method) {
        mutableInstrumentations2[method] = createIterableMethod(method, false, false);
        readonlyInstrumentations2[method] = createIterableMethod(method, true, false);
        shallowInstrumentations2[method] = createIterableMethod(method, false, true);
        shallowReadonlyInstrumentations2[method] = createIterableMethod(method, true, true);
    });
    return [
        mutableInstrumentations2,
        readonlyInstrumentations2,
        shallowInstrumentations2,
        shallowReadonlyInstrumentations2
    ];
}
var _b = __read(/* @__PURE__ */ createInstrumentations(), 4), mutableInstrumentations = _b[0], readonlyInstrumentations = _b[1], shallowInstrumentations = _b[2], shallowReadonlyInstrumentations = _b[3];
function createInstrumentationGetter(isReadonly, shallow) {
    var instrumentations = shallow ? isReadonly ? shallowReadonlyInstrumentations : shallowInstrumentations : isReadonly ? readonlyInstrumentations : mutableInstrumentations;
    return function (target, key, receiver) {
        if (key === "__v_isReactive") {
            return !isReadonly;
        }
        else if (key === "__v_isReadonly") {
            return isReadonly;
        }
        else if (key === "__v_raw") {
            return target;
        }
        return Reflect.get(hasOwn(instrumentations, key) && key in target ? instrumentations : target, key, receiver);
    };
}
var mutableCollectionHandlers = {
    get: /* @__PURE__ */ createInstrumentationGetter(false, false)
};
var shallowCollectionHandlers = {
    get: /* @__PURE__ */ createInstrumentationGetter(false, true)
};
var readonlyCollectionHandlers = {
    get: /* @__PURE__ */ createInstrumentationGetter(true, false)
};
var shallowReadonlyCollectionHandlers = {
    get: /* @__PURE__ */ createInstrumentationGetter(true, true)
};
function checkIdentityKeys(target, has2, key) {
    var rawKey = toRaw(key);
    if (rawKey !== key && has2.call(target, rawKey)) {
        var type = toRawType(target);
        warn("Reactive ".concat(type, " contains both the raw and reactive versions of the same object").concat(type === "Map" ? " as keys" : "", ", which can lead to inconsistencies. Avoid differentiating between the raw and reactive versions of an object and only use the reactive version if possible."));
    }
}
var reactiveMap = /* @__PURE__ */ new WeakMap();
var shallowReactiveMap = /* @__PURE__ */ new WeakMap();
var readonlyMap = /* @__PURE__ */ new WeakMap();
var shallowReadonlyMap = /* @__PURE__ */ new WeakMap();
function targetTypeMap(rawType) {
    switch (rawType) {
        case "Object":
        case "Array":
            return 1 /* COMMON */;
        case "Map":
        case "Set":
        case "WeakMap":
        case "WeakSet":
            return 2 /* COLLECTION */;
        default:
            return 0 /* INVALID */;
    }
}
function getTargetType(value) {
    return value["__v_skip"] || !Object.isExtensible(value) ? 0 /* INVALID */ : targetTypeMap(toRawType(value));
}
function reactive(target) {
    if (isReadonly(target)) {
        return target;
    }
    return createReactiveObject(target, false, mutableHandlers, mutableCollectionHandlers, reactiveMap);
}
function shallowReactive(target) {
    return createReactiveObject(target, false, shallowReactiveHandlers, shallowCollectionHandlers, shallowReactiveMap);
}
function readonly(target) {
    return createReactiveObject(target, true, readonlyHandlers, readonlyCollectionHandlers, readonlyMap);
}
function shallowReadonly(target) {
    return createReactiveObject(target, true, shallowReadonlyHandlers, shallowReadonlyCollectionHandlers, shallowReadonlyMap);
}
function createReactiveObject(target, isReadonly2, baseHandlers, collectionHandlers, proxyMap) {
    if (!isObject(target)) {
        if (!!(process.env.NODE_ENV !== "production")) {
            warn("value cannot be made reactive: ".concat(String(target)));
        }
        return target;
    }
    if (target["__v_raw"] && !(isReadonly2 && target["__v_isReactive"])) {
        return target;
    }
    var existingProxy = proxyMap.get(target);
    if (existingProxy) {
        return existingProxy;
    }
    var targetType = getTargetType(target);
    if (targetType === 0 /* INVALID */) {
        return target;
    }
    var proxy = new Proxy(target, targetType === 2 /* COLLECTION */ ? collectionHandlers : baseHandlers);
    proxyMap.set(target, proxy);
    return proxy;
}
function isReactive(value) {
    if (isReadonly(value)) {
        return isReactive(value["__v_raw"]);
    }
    return !!(value && value["__v_isReactive"]);
}
function isReadonly(value) {
    return !!(value && value["__v_isReadonly"]);
}
function isShallow(value) {
    return !!(value && value["__v_isShallow"]);
}
function isProxy(value) {
    return value ? !!value["__v_raw"] : false;
}
function toRaw(observed) {
    var raw = observed && observed["__v_raw"];
    return raw ? toRaw(raw) : observed;
}
function markRaw(value) {
    if (Object.isExtensible(value)) {
        def(value, "__v_skip", true);
    }
    return value;
}
var toReactive = function (value) { return isObject(value) ? reactive(value) : value; };
var toReadonly = function (value) { return isObject(value) ? readonly(value) : value; };
var COMPUTED_SIDE_EFFECT_WARN = "Computed is still dirty after getter evaluation, likely because a computed is mutating its own dependency in its getter. State mutations in computed getters should be avoided.  Check the docs for more details: https://vuejs.org/guide/essentials/computed.html#getters-should-be-side-effect-free";
var ComputedRefImpl = /** @class */ (function () {
    function ComputedRefImpl(getter, _setter, isReadonly, isSSR) {
        var _this = this;
        this.getter = getter;
        this._setter = _setter;
        this.dep = void 0;
        this.__v_isRef = true;
        this["__v_isReadonly"] = false;
        this.effect = new ReactiveEffect(function () { return getter(_this._value); }, function () { return triggerRefValue(_this, _this.effect._dirtyLevel === 2 ? 2 : 3); });
        this.effect.computed = this;
        this.effect.active = this._cacheable = !isSSR;
        this["__v_isReadonly"] = isReadonly;
    }
    Object.defineProperty(ComputedRefImpl.prototype, "value", {
        get: function () {
            var self = toRaw(this);
            if ((!self._cacheable || self.effect.dirty) && hasChanged(self._value, self._value = self.effect.run())) {
                triggerRefValue(self, 4);
            }
            trackRefValue(self);
            if (self.effect._dirtyLevel >= 2) {
                if (!!(process.env.NODE_ENV !== "production") && this._warnRecursive) {
                    warn(COMPUTED_SIDE_EFFECT_WARN, "\n\ngetter: ", this.getter);
                }
                triggerRefValue(self, 2);
            }
            return self._value;
        },
        set: function (newValue) {
            this._setter(newValue);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ComputedRefImpl.prototype, "_dirty", {
        // #region polyfill _dirty for backward compatibility third party code for Vue <= 3.3.x
        get: function () {
            return this.effect.dirty;
        },
        set: function (v) {
            this.effect.dirty = v;
        },
        enumerable: false,
        configurable: true
    });
    return ComputedRefImpl;
}());
function computed(getterOrOptions, debugOptions, isSSR) {
    if (isSSR === void 0) { isSSR = false; }
    var getter;
    var setter;
    var onlyGetter = isFunction(getterOrOptions);
    if (onlyGetter) {
        getter = getterOrOptions;
        setter = !!(process.env.NODE_ENV !== "production") ? function () {
            warn("Write operation failed: computed value is readonly");
        } : NOOP;
    }
    else {
        getter = getterOrOptions.get;
        setter = getterOrOptions.set;
    }
    var cRef = new ComputedRefImpl(getter, setter, onlyGetter || !setter, isSSR);
    if (!!(process.env.NODE_ENV !== "production") && debugOptions && !isSSR) {
        cRef.effect.onTrack = debugOptions.onTrack;
        cRef.effect.onTrigger = debugOptions.onTrigger;
    }
    return cRef;
}
function trackRefValue(ref2) {
    var _a;
    if (shouldTrack && activeEffect) {
        ref2 = toRaw(ref2);
        trackEffect(activeEffect, (_a = ref2.dep) != null ? _a : ref2.dep = createDep(function () { return ref2.dep = void 0; }, ref2 instanceof ComputedRefImpl ? ref2 : void 0), !!(process.env.NODE_ENV !== "production") ? {
            target: ref2,
            type: "get",
            key: "value"
        } : void 0);
    }
}
function triggerRefValue(ref2, dirtyLevel, newVal) {
    if (dirtyLevel === void 0) { dirtyLevel = 4; }
    ref2 = toRaw(ref2);
    var dep = ref2.dep;
    if (dep) {
        triggerEffects(dep, dirtyLevel, !!(process.env.NODE_ENV !== "production") ? {
            target: ref2,
            type: "set",
            key: "value",
            newValue: newVal
        } : void 0);
    }
}
function isRef(r) {
    return !!(r && r.__v_isRef === true);
}
function ref(value) {
    return createRef(value, false);
}
function shallowRef(value) {
    return createRef(value, true);
}
function createRef(rawValue, shallow) {
    if (isRef(rawValue)) {
        return rawValue;
    }
    return new RefImpl(rawValue, shallow);
}
var RefImpl = /** @class */ (function () {
    function RefImpl(value, __v_isShallow) {
        this.__v_isShallow = __v_isShallow;
        this.dep = void 0;
        this.__v_isRef = true;
        this._rawValue = __v_isShallow ? value : toRaw(value);
        this._value = __v_isShallow ? value : toReactive(value);
    }
    Object.defineProperty(RefImpl.prototype, "value", {
        get: function () {
            trackRefValue(this);
            return this._value;
        },
        set: function (newVal) {
            var useDirectValue = this.__v_isShallow || isShallow(newVal) || isReadonly(newVal);
            newVal = useDirectValue ? newVal : toRaw(newVal);
            if (hasChanged(newVal, this._rawValue)) {
                this._rawValue = newVal;
                this._value = useDirectValue ? newVal : toReactive(newVal);
                triggerRefValue(this, 4, newVal);
            }
        },
        enumerable: false,
        configurable: true
    });
    return RefImpl;
}());
function triggerRef(ref2) {
    triggerRefValue(ref2, 4, !!(process.env.NODE_ENV !== "production") ? ref2.value : void 0);
}
function unref(ref2) {
    return isRef(ref2) ? ref2.value : ref2;
}
function toValue(source) {
    return isFunction(source) ? source() : unref(source);
}
var shallowUnwrapHandlers = {
    get: function (target, key, receiver) { return unref(Reflect.get(target, key, receiver)); },
    set: function (target, key, value, receiver) {
        var oldValue = target[key];
        if (isRef(oldValue) && !isRef(value)) {
            oldValue.value = value;
            return true;
        }
        else {
            return Reflect.set(target, key, value, receiver);
        }
    }
};
function proxyRefs(objectWithRefs) {
    return isReactive(objectWithRefs) ? objectWithRefs : new Proxy(objectWithRefs, shallowUnwrapHandlers);
}
var CustomRefImpl = /** @class */ (function () {
    function CustomRefImpl(factory) {
        var _this = this;
        this.dep = void 0;
        this.__v_isRef = true;
        var _b = factory(function () { return trackRefValue(_this); }, function () { return triggerRefValue(_this); }), get = _b.get, set = _b.set;
        this._get = get;
        this._set = set;
    }
    Object.defineProperty(CustomRefImpl.prototype, "value", {
        get: function () {
            return this._get();
        },
        set: function (newVal) {
            this._set(newVal);
        },
        enumerable: false,
        configurable: true
    });
    return CustomRefImpl;
}());
function customRef(factory) {
    return new CustomRefImpl(factory);
}
function toRefs(object) {
    if (!!(process.env.NODE_ENV !== "production") && !isProxy(object)) {
        warn("toRefs() expects a reactive object but received a plain one.");
    }
    var ret = isArray(object) ? new Array(object.length) : {};
    for (var key in object) {
        ret[key] = propertyToRef(object, key);
    }
    return ret;
}
var ObjectRefImpl = /** @class */ (function () {
    function ObjectRefImpl(_object, _key, _defaultValue) {
        this._object = _object;
        this._key = _key;
        this._defaultValue = _defaultValue;
        this.__v_isRef = true;
    }
    Object.defineProperty(ObjectRefImpl.prototype, "value", {
        get: function () {
            var val = this._object[this._key];
            return val === void 0 ? this._defaultValue : val;
        },
        set: function (newVal) {
            this._object[this._key] = newVal;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ObjectRefImpl.prototype, "dep", {
        get: function () {
            return getDepFromReactive(toRaw(this._object), this._key);
        },
        enumerable: false,
        configurable: true
    });
    return ObjectRefImpl;
}());
var GetterRefImpl = /** @class */ (function () {
    function GetterRefImpl(_getter) {
        this._getter = _getter;
        this.__v_isRef = true;
        this.__v_isReadonly = true;
    }
    Object.defineProperty(GetterRefImpl.prototype, "value", {
        get: function () {
            return this._getter();
        },
        enumerable: false,
        configurable: true
    });
    return GetterRefImpl;
}());
function toRef(source, key, defaultValue) {
    if (isRef(source)) {
        return source;
    }
    else if (isFunction(source)) {
        return new GetterRefImpl(source);
    }
    else if (isObject(source) && arguments.length > 1) {
        return propertyToRef(source, key, defaultValue);
    }
    else {
        return ref(source);
    }
}
function propertyToRef(source, key, defaultValue) {
    var val = source[key];
    return isRef(val) ? val : new ObjectRefImpl(source, key, defaultValue);
}
var deferredComputed = computed;
var TrackOpTypes = {
    "GET": "get",
    "HAS": "has",
    "ITERATE": "iterate"
};
var TriggerOpTypes = {
    "SET": "set",
    "ADD": "add",
    "DELETE": "delete",
    "CLEAR": "clear"
};
var ReactiveFlags = {
    "SKIP": "__v_skip",
    "IS_REACTIVE": "__v_isReactive",
    "IS_READONLY": "__v_isReadonly",
    "IS_SHALLOW": "__v_isShallow",
    "RAW": "__v_raw"
};
export { EffectScope, ITERATE_KEY, ReactiveEffect, ReactiveFlags, TrackOpTypes, TriggerOpTypes, computed, customRef, deferredComputed, effect, effectScope, enableTracking, getCurrentScope, isProxy, isReactive, isReadonly, isRef, isShallow, markRaw, onScopeDispose, pauseScheduling, pauseTracking, proxyRefs, reactive, readonly, ref, resetScheduling, resetTracking, shallowReactive, shallowReadonly, shallowRef, stop, toRaw, toRef, toRefs, toValue, track, trigger, triggerRef, unref };
